import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { SectionTitle, Pagination, Select, Input, Spin, Alert } from "@multiagentbook/ui";

// Simple debounce implementation
const debounce = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};
// import data from "../../data/news.json"; // Removed static import
import { NewsItem } from "../common/types";
import { formatDate } from "../utils";
import DetailView from "./detailview";
import NewsRow from "./newsrow";
import HighlightCard from "./highlightcard";

interface NewsHomeProps {
  isComponent?: boolean;
}

const NewsHome: React.FC<NewsHomeProps> = ({ isComponent = true }) => {
  const { t } = useTranslation("common");
  const [allNewsData, setAllNewsData] = useState<NewsItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedArticle, setSelectedArticle] = useState<NewsItem | null>(null);
  const [filteredData, setFilteredData] = useState<NewsItem[]>([]);
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [weeklyHighlights, setWeeklyHighlights] = useState<NewsItem[]>([]);
  const [highlightDateRange, setHighlightDateRange] = useState<string>("");
  // 8 was sized for tall image cards; rows are about a third the height, so a
  // page holds more without more scrolling — and 82 items over 11 pages was a
  // lot of clicking for a curated feed.
  const pageSize: number = 20;
  // the data supports these and nothing surfaced them: 28 items have a paper,
  // 32 have a repo
  const [onlyPaper, setOnlyPaper] = useState<boolean>(false);
  const [onlyCode, setOnlyCode] = useState<boolean>(false);

  useEffect(() => {
    const fetchNewsData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch("/data/news.json");
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const jsonData = await response.json();
        setAllNewsData(jsonData);
      } catch (e: any) {
        setError(e.message || "Failed to fetch news data.");
        console.error("Failed to fetch news data:", e);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNewsData();
  }, []);

  useEffect(() => {
    if (allNewsData.length === 0) {
      setFilteredData([]);
      setWeeklyHighlights([]);
      return;
    }
    // Sort data by date in descending order
    const sortedData = [...allNewsData].sort(
      (a, b) =>
        new Date(b.launch_date).getTime() - new Date(a.launch_date).getTime()
    );

    // Get highlights from the past 7 days
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    const highlights = sortedData.filter(
      (item) => new Date(item.launch_date) >= oneWeekAgo
    );
    setWeeklyHighlights(highlights.slice(0, 3)); // Limit to top 3 highlights

    // Set the highlight date range
    const startDate = formatDate(oneWeekAgo.toISOString());
    const endDate = formatDate(new Date().toISOString());
    setHighlightDateRange(`${startDate} - ${endDate}`);

    // Apply filters
    let filtered = sortedData;

    if (tagFilter.length > 0) {
      filtered = filtered.filter((item) =>
        tagFilter.every((tag) => item.tags.includes(tag))
      );
    }

    if (searchText) {
      filtered = filtered.filter(
        (item) =>
          item.title.toLowerCase().includes(searchText.toLowerCase()) ||
          item.description.toLowerCase().includes(searchText.toLowerCase()) ||
          item.authors?.some((author: string) =>
            author.toLowerCase().includes(searchText.toLowerCase())
          )
      );
    }

    if (onlyPaper) filtered = filtered.filter((i: NewsItem) => !!i.paper_pdf_url);
    if (onlyCode) filtered = filtered.filter((i: NewsItem) => !!i.github_url);
    setFilteredData(filtered);
  }, [allNewsData, tagFilter, searchText, onlyPaper, onlyCode]);

  useEffect(() => {
    if (allNewsData.length === 0) return;

    const handleUrlChange = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const articleSlug = urlParams.get("article");
      if (articleSlug) {
        const article = allNewsData.find((item) => item.slug === articleSlug);
        if (article) {
          setSelectedArticle(article);
        }
      } else {
        setSelectedArticle(null);
      }
    };

    handleUrlChange(); // Initial check

    if (typeof window !== "undefined") {
      window.addEventListener("popstate", handleUrlChange);
      return () => window.removeEventListener("popstate", handleUrlChange);
    }
  }, [allNewsData]);

  const handlePageChange = (page: number): void => {
    setCurrentPage(page);
  };

  const handleCardClick = (article: NewsItem): void => {
    if (typeof window !== "undefined") {
      window.history.pushState(null, "", `/news/?article=${article.slug}`);
    }
    setSelectedArticle(article);
  };

  const handleBackToGrid = (): void => {
    setSelectedArticle(null);
    if (typeof window !== "undefined") {
      window.history.pushState(null, "", "/news");
    }
  };

  const handleTagFilterChange = (tags: string[]) => {
    setTagFilter(tags);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setTagFilter([]);
    setOnlyPaper(false);
    setOnlyCode(false);
    setCurrentPage(1);
  };

  const debouncedSearch = debounce((value: string) => {
    setSearchText(value);
  }, 300);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };

  const paginatedData: NewsItem[] = filteredData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // counts so a tag says how much is behind it before you click
  const tagCounts = allNewsData.reduce((acc: Record<string, number>, item) => {
    (item.tags || []).forEach((t: string) => (acc[t] = (acc[t] || 0) + 1));
    return acc;
  }, {});
  const allTags = Object.keys(tagCounts).sort((a, b) => tagCounts[b] - tagCounts[a]);
  const paperCount = allNewsData.filter((i) => i.paper_pdf_url).length;
  const codeCount = allNewsData.filter((i) => i.github_url).length;
  const filtersActive = tagFilter.length > 0 || onlyPaper || onlyCode;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" tip={t("news.loadingNews")} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-20">
        <Alert message={t("news.error")} description={error} type="error" showIcon />
      </div>
    );
  }

  return (
    <div
      className={`${
        isComponent ? "border-t border-secondary mt-20 pt-20 my-10" : "my-20"
      }`}
    >
      <SectionTitle title={t("news.title")} />
      <div className="text-secondary py-2 mb-4">
        {t("news.description")}
      </div>
      {selectedArticle ? (
        <DetailView article={selectedArticle} onBack={handleBackToGrid} />
      ) : (
        <>
          {weeklyHighlights.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">
                {t("news.weeklyHighlights")} ({highlightDateRange})
              </h2>
              <div className="border-b pb-8 border-dashed grid grid-cols-1 md:grid-cols-3 gap-4">
                {weeklyHighlights.map((highlight, index) => (
                  <HighlightCard
                    key={index}
                    news={highlight}
                    onClick={handleCardClick}
                  />
                ))}
              </div>
            </div>
          )}
          <div className="mb-6">
            <Input.Search
              className="w-full mb-4"
              placeholder={t("news.searchPlaceholder")}
              allowClear
              onChange={handleSearchChange}
            />
            <div className="flex flex-wrap gap-2 items-center">
              {allTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => {
                    const isSelected = tagFilter.includes(tag);
                    if (isSelected) {
                      handleTagFilterChange(tagFilter.filter((t) => t !== tag));
                    } else {
                      handleTagFilterChange([...tagFilter, tag]);
                    }
                  }}
                  className={`px-3 py-1 text-xs rounded border transition-colors ${
                    tagFilter.includes(tag)
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200"
                  }`}
                >
                  {tag} <span className="opacity-60">{tagCounts[tag]}</span>
                </button>
              ))}
            </div>

            {/* A different kind of filter from a tag, so it does not sit in the
                tag row wearing the same pill. Years were here too and are gone:
                the list is already chronological and paginated. */}
            <div className="flex flex-wrap gap-2 items-center mt-3">
              <span className="text-xs text-secondary mr-1">Only with</span>
              <button
                onClick={() => { setOnlyPaper(!onlyPaper); setCurrentPage(1); }}
                className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                  onlyPaper
                    ? "bg-blue-600 text-white border-blue-600"
                    : "border-secondary text-secondary hover:text-primary"
                }`}
              >
                a paper <span className="opacity-60">{paperCount}</span>
              </button>
              <button
                onClick={() => { setOnlyCode(!onlyCode); setCurrentPage(1); }}
                className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                  onlyCode
                    ? "bg-blue-600 text-white border-blue-600"
                    : "border-secondary text-secondary hover:text-primary"
                }`}
              >
                code <span className="opacity-60">{codeCount}</span>
              </button>
            </div>

            <div className="flex items-center gap-3 mt-3 text-xs text-secondary">
              <span>
                {filteredData.length} of {allNewsData.length} items
              </span>
              {filtersActive && (
                <button onClick={clearFilters} className="text-accent hover:underline">
                  clear filters
                </button>
              )}
            </div>
          </div>
          <ul role="list" className="border-t border-secondary">
            {paginatedData.map((news: NewsItem, i: number) => (
              <NewsRow key={news.slug || i} news={news} onClick={handleCardClick} />
            ))}
          </ul>
          <div className="mt-8 flex justify-center">
            <Pagination
              current={currentPage}
              total={filteredData.length}
              pageSize={pageSize}
              onChange={handlePageChange}
              showSizeChanger={false}
              showTotal={(total) => (
                <span className="text-secondary">
                  {t("news.showing", {
                    start: pageSize * (currentPage - 1) + 1,
                    end: Math.min(pageSize * currentPage, total),
                    total
                  })}
                </span>
              )}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default NewsHome;
